/**
 * The main nav shows only what is in stock.
 *
 * Liquid prunes a menu link whose collection has no products, but a
 * collection's total counts out-of-stock products too. This asks the
 * store for each linked collection's in-stock count (templates/
 * collection.count.liquid with the Availability filter), hides a link —
 * and a whole entry such as Riding Gear — with nothing in stock, and shows
 * the in-stock number on the badge (Lucas, 2026-10-13).
 *
 * Counts are cached in localStorage for an hour, applied before the first
 * paint of the menu on later page views, and refreshed a moment after load
 * so a page never waits on it. Nothing is hidden on an error: an unknown
 * count is not "none in stock".
 */
(function () {
  'use strict';

  const CACHE_KEY = 'fmt:nav-stock';
  const TTL = 60 * 60 * 1000;
  const CONCURRENCY = 3;
  const SELECTOR = '.mega-menu__links a[href*="/collections/"], .mega-menu__trigger--link[href*="/collections/"], .brand-directory [data-brand-item] a.brand-tile[href*="/collections/"]';

  function readCache() {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      const data = raw ? JSON.parse(raw) : null;
      return data && typeof data === 'object' ? data : {};
    } catch { return {}; }
  }

  function writeCache(data) {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch { /* blocked or full */ }
  }

  function handleOf(link) {
    try {
      const path = new URL(link.getAttribute('href'), window.location.origin).pathname;
      const m = path.match(/\/collections\/([^/]+)/);
      return m ? decodeURIComponent(m[1]) : null;
    } catch { return null; }
  }

  function links() {
    return [...document.querySelectorAll(SELECTOR)];
  }

  /**
   * A brands-page section narrowed to a collection (request 168): its
   * tiles lead to that collection filtered to the brand, and count there.
   */
  function areaOf(link) {
    const area = link.closest('[data-brand-area-collection]');
    if (!area) return null;
    const handle = area.dataset.brandAreaCollection;
    return { handle, url: area.dataset.brandAreaUrl || `/collections/${encodeURIComponent(handle)}` };
  }

  /** The cache key: the collection handle, or "<area>|vendor:<Brand>" for a narrowed tile. */
  function keyOf(link) {
    const area = areaOf(link);
    if (area) return `${area.handle}|vendor:${link.dataset.brandMake || ''}`;
    return link.dataset.brand || handleOf(link);
  }

  function countUrl(key) {
    const [handle, vendor] = key.split('|vendor:');
    const base = `/collections/${encodeURIComponent(handle)}?view=count&filter.v.availability=1`;
    return vendor === undefined ? base : `${base}&filter.p.vendor=${encodeURIComponent(vendor)}`;
  }

  /** Point a narrowed tile at its area, filtered to the brand, in stock. */
  function aim(link) {
    const area = areaOf(link);
    if (!area || link.dataset.aimed) return;
    link.href = `${area.url}?filter.p.vendor=${encodeURIComponent(link.dataset.brandMake || '')}&filter.v.availability=1`;
    link.dataset.aimed = '';
  }

  /**
   * A brand tile in a section without a collection, on a page where the
   * same brand sits in narrowed sections: its count is the brand's total
   * less those sections' counts, so the numbers add up (request 168).
   */
  function siblingsOf(link) {
    const handle = link.dataset.brand;
    if (!handle || areaOf(link) || !link.closest('.brand-directory')) return [];
    return [...document.querySelectorAll('.brand-directory [data-brand-area-collection] a.brand-tile')].filter((l) => l.dataset.brand === handle);
  }

  /** Apply every fresh count; a derived one waits for its inputs unless this is the final pass. */
  function applyAll(cache, final) {
    const now = Date.now();
    const fresh = (key) => { const e = key && cache[key]; return e && now - e.at < TTL ? e.n : undefined; };
    for (const link of links()) {
      const n = fresh(keyOf(link));
      if (n === undefined) continue;
      const siblings = siblingsOf(link);
      if (!siblings.length) { apply(link, n); continue; }
      const others = siblings.map((l) => fresh(keyOf(l)));
      if (others.every((v) => v !== undefined)) apply(link, Math.max(0, n - others.reduce((sum, v) => sum + v, 0)));
      else if (final) apply(link, n);
    }
  }

  /**
   * Hide or show a link (its list item, the whole nav entry, or a brand
   * tile's item) and fix its count. A brands-page section whose every
   * brand is hidden goes too (Lucas, 2026-10-14).
   */
  function apply(link, count) {
    const item = link.closest('[data-brand-item]') || link.closest('li') || link.closest('.mega-menu__item') || link;
    item.hidden = count === 0;
    // The brands page's search toggles items too; the flag keeps an
    // out-of-stock brand hidden when a search is cleared.
    item.toggleAttribute('data-out-of-stock', count === 0);
    const badge = link.querySelector('.mega-menu__count');
    if (badge && count > 0) badge.textContent = String(count);
    const tileCount = link.querySelector('[data-brand-stock-count]');
    if (tileCount && count > 0) { tileCount.textContent = `${count} in stock`; tileCount.hidden = false; }
    const group = link.closest('.mega-menu__group');
    if (group) {
      const anyShown = [...group.querySelectorAll('.mega-menu__links li')].some((li) => !li.hidden);
      group.hidden = !anyShown;
    }
    const area = link.closest('.brand-directory__area');
    if (area) {
      const anyBrand = [...area.querySelectorAll('[data-brand-item]')].some((el) => !el.hidden);
      area.hidden = !anyBrand;
    }
  }

  function paint(cache) {
    links().forEach(aim);
    applyAll(cache, false);
  }

  async function count(key) {
    const res = await fetch(countUrl(key), { credentials: 'omit' });
    if (!res.ok) throw new Error(`count ${res.status}`);
    const text = (await res.text()).trim();
    if (!/^\d+$/.test(text)) throw new Error('count malformed');
    return Number(text);
  }

  /** Fetch what the cache lacks or has stale, a few at a time, applying as each answers. */
  async function refresh() {
    const cache = readCache();
    const now = Date.now();
    const wanted = [];
    links().forEach(aim);
    for (const link of links()) {
      const key = keyOf(link);
      if (!key) continue;
      const entry = cache[key];
      if (!entry || now - entry.at >= TTL) wanted.push(key);
    }
    const keys = [...new Set(wanted)];
    let i = 0;
    const worker = async () => {
      while (i < keys.length) {
        const key = keys[i++];
        try {
          const n = await count(key);
          cache[key] = { n, at: Date.now() };
          applyAll(cache, false);
        } catch { /* unknown stays visible */ }
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, keys.length) }, worker));
    applyAll(cache, true);
    writeCache(cache);
    // The brands page recounts "N brands" from what is left showing.
    document.dispatchEvent(new CustomEvent('nav-stock:updated'));
    return cache;
  }

  function boot() {
    paint(readCache());
    const start = () => { refresh(); };
    if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 3000 });
    else setTimeout(start, 1500);
  }

  window.NavStock = { refresh, paint, apply, handleOf, keyOf };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
